import { useState, useRef, useEffect } from 'react'
import { Modal } from '@/shared/components/Modal'
import { Tooltip } from '@/shared/components/Tooltip'
import { useSelectedOrg } from '@/context/SelectedOrgContext'
import { api } from '@/lib/api'

export function BetaEnterpriseUpgradeModal({ orgSlug, orgName, onClose, onUpgraded }: {
  orgSlug: string
  orgName: string
  onClose: () => void
  onUpgraded: () => void
}) {
  // Same self-contained pattern as CoderTemplatesModal: feedback is inline
  // because a toast can't paint over a native <dialog>.
  const [state, setState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const mountedRef = useRef(true)
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false } }, [])

  async function upgrade() {
    setState('submitting')
    setErrorMsg('')
    try {
      await api.post(`/api/orgs/${orgSlug}/billing/beta-upgrade`, { tier: 'enterprise' })
      if (mountedRef.current) setState('success')
    } catch (err) {
      if (!mountedRef.current) return
      setState('error')
      setErrorMsg(err instanceof Error ? err.message : 'Network error. Please try again.')
    }
  }

  return (
    <Modal track="beta_enterprise_upgrade" title="Upgrade to Enterprise" onClose={onClose}>
      <p className="guide-para">
        This switches{' '}
        <strong>{orgName}</strong> to the Enterprise tier straight away - no card, no commitment.
      </p>
      <ul className="guide-list">
        <li>Unlimited modules, providers, and members</li>
        <li>OPA policy gates, audit log, SSO, and Slack notifications</li>
        <li>Provider builds, scanning, and the state backend</li>
      </ul>
      <div className="callout-accent">
        <strong>Nothing is charged automatically.</strong>
      </div>
      {state === 'success' ? (
        <div style={{ marginTop: 16 }}>
          <div className="feedback-success">Done - {orgName} is now on the Enterprise tier.</div>
          <div className="modal-actions">
            <button className="btn btn-primary btn-sm" onClick={onUpgraded}>Close</button>
          </div>
        </div>
      ) : (
        <>
          {state === 'error' && (
            <div className="feedback-error" style={{ marginTop: 10 }}>{errorMsg}</div>
          )}
          <div className="modal-actions">
            <button className="btn btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={upgrade} disabled={state === 'submitting'}>
              {state === 'submitting' ? 'Upgrading…' : 'Upgrade'}
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}

export function BetaEnterpriseUpgradeButton() {
  const { selectedOrg, refetchOrgs } = useSelectedOrg()
  const [show, setShow] = useState(false)

  const eligible = selectedOrg
    && ['owner', 'admin'].includes(selectedOrg.role)
    && selectedOrg.is_personal !== 1
    && selectedOrg.is_demo !== 1
    && selectedOrg.tier !== 'enterprise'
  if (!eligible || !selectedOrg) return null

  return (
    <>
      {show && (
        <BetaEnterpriseUpgradeModal
          orgSlug={selectedOrg.slug}
          orgName={selectedOrg.name}
          onClose={() => setShow(false)}
          onUpgraded={() => { setShow(false); refetchOrgs() }}
        />
      )}
      <Tooltip text="Switch this org to Enterprise - no card required" placement="bottom">
        <button className="btn btn-primary btn-sm" onClick={() => setShow(true)}>
          Upgrade to Enterprise
        </button>
      </Tooltip>
    </>
  )
}

